<template>
    <div class="flex items-stretch w-full items-center">
        <BtnLike
            :enabled="!post.isOwner"
            :active="post.postDetails.userLiked"
            :likes="post.postDetails.upvotes"
            :request-url="
                route(`post.question.upvote`, {
                    context,
                    id: post.postDetails.id,
                })
            "
            size="sm"
            class="mr-5 md:mr-6"
        />
        <Btn
            variant="text"
            class="!font-normal mr-5 md:mr-6"
            content-color="slate-600"
            icon="discussions"
            :size="BtnSize.SM"
            :label="commentsBtnLabel"
            compact
            @click="emitFocusCommentInputEvent"
        />
        <BtnMenuShare
            v-if="screen.maxSM"
            data-testid="post-footer_btn-menu-share"
            :share-title="
                t('share.title', {
                    name: contextData.name,
                })
            "
            :share-url="shareUrl"
            variant="tonal"
            color="primary"
            color-on-touch
            size="sm"
        />
        <BtnMenu
            v-if="screen.maxSM"
            color-on-touch
            variant="tonal"
            color="primary"
            size="sm"
            left-icon="more"
            :options="conditionalmenuItems"
        />
        <Btn
            v-if="shouldShowScrollToMarkingOption"
            :variant="BtnVariant.OUTLINED"
            class="ml-auto uppercase !font-medium !text-xs"
            :color="pdfViewerInitialized ? 'orange-200' : 'slate-100'"
            :content-color="pdfViewerInitialized ? 'yellow-500' : null"
            :size="BtnSize.XS"
            :label="t('post:seeMark')"
            :disabled="!pdfViewerInitialized"
            @click="emitScrollToMark"
        />
        <Btn
            v-else-if="shouldShowHelpButton"
            :variant="BtnVariant.OUTLINED"
            class="ml-auto uppercase !font-medium !text-xs"
            color="orange-200"
            content-color="yellow-500"
            :size="BtnSize.XS"
            :label="t('help')"
            @click="emitFocusCommentInputEvent"
        />
        <Btn
            v-else-if="post.postDetails.hasBestAnswer"
            :variant="BtnVariant.OUTLINED"
            class="ml-auto !text-sm w-36"
            color="green-500"
            :icon="mouseIsOverShowAnswerButton ? null : 'checkmark-outlined'"
            :size="BtnSize.SM"
            :label="mouseIsOverShowAnswerButton ? t('showAnswer') : t('answered')"
            @click="emitScrollToBestAnswerEvent"
            @mouseleave="mouseIsOverShowAnswerButton = false"
            @mouseover="mouseIsOverShowAnswerButton = true"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import route from '@router/route';
import { BtnSize, BtnVariant } from '@components/Btn';
import BtnLike from '@components/BtnLike';
import stripWhiteSpaceSpecialCharacters from '@/helpers/stripWhiteSpaceSpecialCharacters';
import BtnMenuShare from '@components/BtnMenuShare/BtnMenuShare.vue';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'PostFooter',

    components: {
        BtnMenuShare,
        BtnLike,
    },

    props: {
        post: {
            type: Object,
            required: true,
        },
        hideHelpButton: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'focus-comment-input',
        'scroll-best-answer',
        'scroll-to-mark',
        'delete-post',
        'edit-post',
        'pin-unpin-post',
        'report-post',
    ],

    setup() {
        const { t } = useTranslation('post');
        return { t };
    },

    data() {
        return {
            BtnSize,
            BtnVariant,
            mouseIsOverShowAnswerButton: false,
            menuItems: [
                {
                    icon: 'edit',
                    name: 'edit',
                    onClick: this.emitEditPost,
                    authorizedAction: true,
                },
                {
                    icon: 'pin',
                    name: 'pin',
                    onClick: this.emitPinUnpinPost,
                    authorizedAction: false,
                },
                {
                    icon: 'flag',
                    name: 'report',
                    onClick: this.emitReportPost,
                    authorizedAction: true,
                },
                {
                    icon: 'trash',
                    name: 'delete',
                    onClick: this.emitDeletePost,
                    authorizedAction: true,
                },
            ],
        };
    },

    computed: {
        ...mapGetters('ui', ['screen']),
        ...mapGetters({ currentUser: 'auth/user' }),
        ...mapGetters('auth', ['isAdmin', 'isLoggedIn']),
        ...mapGetters('document', ['pdfViewerInitialized']),

        isPostOwner() {
            return this.post.isOwner;
        },
        commentsBtnLabel() {
            return this.screen.minMD && route().current() !== 'document.show' ? this.t('comment') : null;
        },

        conditionalmenuItems() {
            return this.menuItems
                .filter((items) => items.authorizedAction === true)
                .map((item) => ({
                    ...item,
                    label: this.t(`post:menu:${item.name}`),
                    class: item.name === 'reported' ? 'text-red-400 pointer-events-none cursor-default' : '',
                }));
        },
        context() {
            return this.post.postDetails.context;
        },
        contextData() {
            return this.post[this.context];
        },
        hasPinnedPost() {
            return this.$store.getters[`${this.context}/hasPinnedPost`];
        },
        markings() {
            return this.post.postDetails?.markings;
        },
        shouldShowHelpButton() {
            return !this.hideHelpButton && this.post.totalComments === 0;
        },
        shouldShowScrollToMarkingOption() {
            return this.markings && this.context === 'document';
        },
        shareUrl() {
            const userID = this.isLoggedIn ? this.currentUser.userid : null;
            return `${this.post.postDetails.url}?ref=${userID}`;
        },
        user() {
            return this.post.postDetails.user;
        },
        reportedState() {
            const reportedState = this.post.postDetails.isReported && this.post.postDetails.userReport;
            return reportedState;
        },
    },

    beforeMount() {
        this.setAuthorizedActions();
        this.setReported();
    },

    methods: {
        route,
        setReported() {
            if (this.reportedState) {
                this.menuItems[2].name = 'reported';
            }
        },
        setAuthorizedActions() {
            if (this.isLoggedIn) {
                // edit Post
                this.menuItems[0].authorizedAction = this.isPostOwner || this.isAdmin;
                // pin Post
                if (!this.isAdmin) {
                    this.menuItems[1].authorizedAction = false;
                } else {
                    // eslint-disable-next-line no-lonely-if
                    if (this.hasPinnedPost && !this.post.isPinned) {
                        this.menuItems[1].authorizedAction = false;
                    }
                }
                if (this.post.isPinned) {
                    this.menuItems[1].name = 'unpin';
                }
                // report Post
                this.menuItems[2].authorizedAction = !this.isPostOwner;
                // delete Post
                this.menuItems[3].authorizedAction = this.isPostOwner || this.isAdmin;
            }
            // edit Post
            this.menuItems[0].authorizedAction = this.isPostOwner || this.isAdmin;
            // pin Post
            if (
                this.isAdmin &&
                !this.hasPinnedPost &&
                (route().current() !== 'document.page' || route().current() !== 'newsfeed.show')
            ) {
                this.menuItems[1].authorizedAction = true;
            } else if (this.isAdmin && this.hasPinnedPost && this.post.isPinned) {
                this.menuItems[1].authorizedAction = true;
            }
            if (this.post.isPinned) {
                this.menuItems[1].name = 'unpin';
            }
            // report Post
            this.menuItems[2].authorizedAction = !this.isPostOwner;
            // delete Post
            this.menuItems[3].authorizedAction = this.isPostOwner || this.isAdmin;
        },
        emitFocusCommentInputEvent() {
            this.$emit('focus-comment-input');
        },
        emitScrollToBestAnswerEvent() {
            this.$emit('scroll-best-answer');
        },
        emitScrollToMark() {
            setTimeout(() => {
                // small delay to activate the Question Markings. Otherwise scroll to mark wont work
                this.$emit(
                    'scroll-to-mark',
                    {
                        id: this.post.postDetails.id,
                        marking: this.markings,
                        rotation: 0,
                    },
                    200,
                );
            });
        },
        emitDeletePost() {
            this.$emit('delete-post');
        },
        emitEditPost() {
            this.$emit('edit-post');
        },
        emitPinUnpinPost() {
            this.$emit('pin-unpin-post');
        },
        emitReportPost() {
            this.$emit('report-post');
        },
        stripWhiteSpaceSpecialCharacters,
    },
};
</script>
